import { state } from '@react-rxjs/core'
import {
  concat,
  concatMap,
  distinctUntilChanged,
  EMPTY,
  filter,
  groupBy,
  ignoreElements,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  scan,
  skip,
  startWith,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs'

import {
  AccessTokenFactoryOptions,
  AuthState,
  latestAccessTokenFactory$,
  latestAuthState$,
} from '../auth/state'
import {
  DispensaryUserState,
  dispensaryUserStateChange$,
  DispensaryUserUpdateComplete,
} from '../dispensaries/events/user'
import { latestEScriptCartUpdateStatus } from '../dispensaries/state'
import { createCappedRenew, createRenew, DEFAULT_RENEW_MIN_INTERVAL_MS } from './auth'
import {
  CrossTabChannel,
  crossTabChannel,
  CrossTabEnvelope,
  CrossTabMessage,
  latestVisibleByTag,
  VisibilityGate,
} from './channel'

export type EScriptStatus = Extract<CrossTabMessage, { readonly _tag: 'escript' }>['status']

export type RemoteEScriptStatuses = ReadonlyMap<string, EScriptStatus>

type RemoteEScriptEvent =
  | { readonly _tag: 'status'; readonly source: string; readonly status: EScriptStatus }
  | { readonly _tag: 'expired'; readonly source: string }

const DEFAULT_EXPIRY_MS = 2 * 60 * 1000

// Only the id-token claims need to change, and they come from the Auth0 cache the call refreshes,
// so the new token stays out of the token context, whose every change resets the Apollo store.
export const dispensaryRenewOptions: AccessTokenFactoryOptions = {
  ignoreCache: true,
  updateTokenContext: false,
  allowInteractive: false,
}

const isUserUpdateComplete = (event: DispensaryUserState): event is DispensaryUserUpdateComplete =>
  event.type === 'user_update_complete'

const isSignedIn = (authState: AuthState | null): boolean =>
  authState !== null && !authState.isLoading && authState.isAuthenticated

const foldRemoteEScriptStatus = (
  statuses: RemoteEScriptStatuses,
  event: RemoteEScriptEvent,
): RemoteEScriptStatuses => {
  if (event._tag === 'status') {
    return new Map(statuses).set(event.source, event.status)
  }
  const remaining = new Map(statuses)
  remaining.delete(event.source)
  return remaining
}

const combinedEScriptStatus = (statuses: Iterable<EScriptStatus>): EScriptStatus =>
  Array.from(statuses).includes('in_progress') ? 'in_progress' : 'idle'

/**
 * The eScript status other tabs report, `in_progress` while any of them is. A source's entry
 * expires `expiryMs` after its latest `in_progress` was sent, so a tab closed mid-hydration cannot
 * hold the pause.
 */
export const deriveRemoteEScriptStatus = (
  envelopes$: Observable<CrossTabEnvelope>,
  expiryMs: number,
  now: () => number = Date.now,
): Observable<EScriptStatus> =>
  envelopes$.pipe(
    concatMap(({ source, sentAt, message }) =>
      message._tag === 'escript' ? of({ source, sentAt, status: message.status }) : EMPTY,
    ),
    groupBy(({ source }) => source),
    mergeMap((bySource$) =>
      bySource$.pipe(
        switchMap(({ source, sentAt, status }): Observable<RemoteEScriptEvent> => {
          const received = of<RemoteEScriptEvent>({ _tag: 'status', source, status })
          const expired = of<RemoteEScriptEvent>({ _tag: 'expired', source })
          // Clamped at `expiryMs`, so a `sentAt` ahead of this tab's clock holds the pause for one
          // expiry at most.
          const remainingMs = Math.min(expiryMs, Math.max(0, expiryMs - (now() - sentAt)))
          return status !== 'in_progress'
            ? received
            : remainingMs === 0
              ? expired
              : concat(received, timer(remainingMs).pipe(concatMap(() => expired)))
        }),
      ),
    ),
    scan(foldRemoteEScriptStatus, new Map<string, EScriptStatus>()),
    map((statuses) => combinedEScriptStatus(statuses.values())),
    distinctUntilChanged(),
  )

export interface CrossTabDispensaryDeps {
  readonly channel: CrossTabChannel
  readonly dispensaryUserState$: Observable<DispensaryUserState>
  readonly eScriptStatus$: Observable<EScriptStatus>
  readonly authState$: Observable<AuthState>
  readonly renew$: Observable<unknown>
  readonly visibility?: VisibilityGate
  readonly expiryMs?: number
  readonly now?: () => number
  readonly renewMinIntervalMs?: number
}

export interface CrossTabDispensary {
  readonly sync$: Observable<unknown>
  readonly remoteEScriptCartUpdateStatus$: Observable<EScriptStatus>
}

export const createCrossTabDispensary = ({
  channel,
  dispensaryUserState$,
  eScriptStatus$,
  authState$,
  renew$,
  visibility,
  expiryMs = DEFAULT_EXPIRY_MS,
  now = Date.now,
  renewMinIntervalMs = DEFAULT_RENEW_MIN_INTERVAL_MS,
}: CrossTabDispensaryDeps): CrossTabDispensary => {
  const cappedRenew$ = createCappedRenew(renew$, { now, minIntervalMs: renewMinIntervalMs })
  const dispensaryPublisher$ = dispensaryUserState$.pipe(
    filter(isUserUpdateComplete),
    // No payload: receivers renew their own claims, and the code is health-adjacent.
    tap(() => channel.publish({ _tag: 'dispensary' })),
  )

  // The signal replays its current value: an `idle` a tab that merely loads must not announce, or
  // an `in_progress` already under way, which it must.
  const eScriptPublisher$ = eScriptStatus$.pipe(
    startWith<EScriptStatus>('idle'),
    distinctUntilChanged(),
    skip(1),
    tap((status) => channel.publish({ _tag: 'escript', status })),
  )

  const currentlySignedIn$ = merge(authState$, of(null)).pipe(take(1), map(isSignedIn))

  // A renewal is what brings `practitionerCodeRecent` into this tab's id-token claims.
  const dispensaryConsumer$ = channel.incoming$.pipe(
    latestVisibleByTag(visibility),
    filter(({ message }) => message._tag === 'dispensary'),
    concatMap(() =>
      currentlySignedIn$.pipe(concatMap((signedIn) => (signedIn ? cappedRenew$ : EMPTY))),
    ),
  )

  // Not held while hidden: the pause costs no request, and a hidden checkout tab's background
  // refetch can still hand its promotions machine a cart mid-hydration.
  const remoteEScriptCartUpdateStatus$ = state(
    deriveRemoteEScriptStatus(channel.incoming$, expiryMs, now),
    'idle',
  )

  return {
    // Holding the remote status here keeps its fold running while `global-promos.ts` is not
    // subscribed, so the first subscriber there sees every report already heard.
    sync$: merge(
      dispensaryPublisher$,
      eScriptPublisher$,
      dispensaryConsumer$,
      remoteEScriptCartUpdateStatus$.pipe(ignoreElements()),
    ),
    remoteEScriptCartUpdateStatus$,
  }
}

const crossTabDispensary = createCrossTabDispensary({
  channel: crossTabChannel,
  dispensaryUserState$: dispensaryUserStateChange$,
  eScriptStatus$: latestEScriptCartUpdateStatus,
  authState$: latestAuthState$,
  renew$: createRenew({ accessTokenFactory$: latestAccessTokenFactory$ }, dispensaryRenewOptions),
})

export const crossTabDispensary$: Observable<unknown> = crossTabDispensary.sync$

export const remoteEScriptCartUpdateStatus$: Observable<EScriptStatus> =
  crossTabDispensary.remoteEScriptCartUpdateStatus$
